import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import LinkButton from 'components/common/LinkButton/LinkButton';
import {
  INPUT_DOC_LENS_MARGIN_BREAK_EVEN,
  INPUT_DOC_TAB_INSIGHTS,
  LINK_FULL_SENSITIVITY_LABEL,
  RIGHT_PANEL_TAB_FINANCIAL,
} from 'constants/uiConstants';
import { INPUT_KEY_PREFIX } from 'constants/routeConstants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/Store';
import { useScopedInputs } from 'hooks/useScopedInputs';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import {
  computeFinancialRisk,
  type FinancialRiskResult,
} from 'pages/FarmsPage/Workspace/Insights/computeFinancialRisk';
import {
  computeCropAverageGm,
  type CropAverageGm,
} from 'pages/FarmsPage/Workspace/Insights/computeScopeContext';
import { PLAN_ONLY_CATEGORIES } from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import {
  BreakEvenChartWithLegend,
  BreakEvenRows,
  formatDollar,
  formatDollarFull,
} from './BreakEvenTables';

/* ── Sub-components ─────────────────────────────────────────────────────── */

/** Alternating row shading — same bg-white/bg-gray-50 convention as the other Live totals tables. */
const rowShade = (idx: number): string =>
  idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

/** A dollar metric value (Total Spend / Est. Net Gross Margin), or a dash when there's no data. */
const DollarCell: React.FC<{
  hasData: boolean;
  value: number;
  colorClass?: string;
}> = ({ hasData, value, colorClass }) => {
  if (!hasData) return <span className="text-gray-muted">—</span>;
  return (
    <Tooltip title={formatDollarFull(value)}>
      <span className={`cursor-default ${colorClass ?? ''}`}>
        {formatDollar(value)}
      </span>
    </Tooltip>
  );
};

/** A $/ha value with a muted unit caption, or a dash when there's no figure. */
const PerHaCell: React.FC<{ value: number | null }> = ({ value }) =>
  value === null ? (
    <span className="text-gray-muted">—</span>
  ) : (
    <span className="whitespace-nowrap">
      {formatDollar(value)}
      <span className="ml-1 text-xs font-semibold text-gray-muted">/ha</span>
    </span>
  );

/**
 * "Plan GM vs crop average" section (OP-2392): this plan's expected GM $/ha
 * against the area-weighted average for the same crop and season across the
 * organisation, with the total area behind it so a thin average isn't
 * over-read. The plan is
 * excluded from its own average; with no other plans the section says so
 * rather than comparing the plan with itself.
 */
const CropAverageRows: React.FC<{
  comparison: CropAverageGm;
  stripeOffset: number;
}> = ({ comparison, stripeOffset }) => {
  const { cropSeason, planGmPerHa, averageGmPerHa, averageAreaHa } =
    comparison;
  const seasonLabel = `${cropSeason.cropName} ${cropSeason.year}`;
  const difference =
    planGmPerHa !== null && averageGmPerHa !== null
      ? planGmPerHa - averageGmPerHa
      : null;

  return (
    <>
      <tr>
        <td
          colSpan={2}
          className="border-b pb-1 pr-3 pt-3 text-sm font-semibold text-secondary-dark"
        >
          Plan GM vs crop average
        </td>
      </tr>
      <tr className={rowShade(stripeOffset)}>
        <td className="py-1 pr-3 text-label text-charcoal">This plan</td>
        <td className="py-1 text-right tabular-nums text-charcoal">
          <PerHaCell value={planGmPerHa} />
        </td>
      </tr>
      {averageGmPerHa === null ? (
        <tr className={rowShade(stripeOffset + 1)}>
          <td colSpan={2} className="py-1 pr-3 text-label text-gray-dark">
            No other {seasonLabel} plans in the organisation to compare with
            yet
          </td>
        </tr>
      ) : (
        <>
          <tr className={rowShade(stripeOffset + 1)}>
            <td className="py-1 pr-3 text-label text-charcoal">
              <span className="inline-flex flex-col">
                <span>{seasonLabel} average</span>
                <span className="whitespace-nowrap text-sm text-gray-dark">
                  {averageAreaHa.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })}{' '}
                  ha across the organisation
                </span>
              </span>
            </td>
            <td className="py-1 text-right tabular-nums text-charcoal">
              <PerHaCell value={averageGmPerHa} />
            </td>
          </tr>
          {difference !== null && (
            <tr className={rowShade(stripeOffset + 2)}>
              <td className="py-1 pr-3 text-label text-charcoal">
                Difference
              </td>
              <td
                className={`py-1 text-right tabular-nums font-medium ${
                  difference >= 0 ? 'text-success' : 'text-danger'
                }`}
              >
                {/* Colour is paired with a word — never status colour alone. */}
                {formatDollar(Math.abs(difference))}/ha{' '}
                {difference >= 0 ? 'above' : 'below'}
              </td>
            </tr>
          )}
        </>
      )}
    </>
  );
};

/** Number of striped data rows CropAverageRows renders, so rows below it keep the stripe continuous. */
const cropAverageRowCount = (comparison: CropAverageGm): number => {
  if (comparison.averageGmPerHa === null) return 2;
  return comparison.planGmPerHa !== null ? 3 : 2;
};

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * Right-panel tab for Financial Risk summary (OP-2273).
 *
 * Displays key metrics derived from aggregating computeGrossMargin across all
 * scoped inputs:
 *
 *   Total Spend    — committed input cost ($)
 *   Est. Net Gross Margin    — expected gross margin at mid yield × mid price ($)
 *   Plan GM vs crop average — plan scope only (OP-2392)
 *   Break-even yield  — yield/ha needed to cover spend at Low/Mid/High price
 *   Break-even price  — price needed to cover spend at Low/Mid/High yield
 *
 * The break-even rows and chart are shared with the Insights "Margin &
 * break-even" lens (OP-2393, see BreakEvenTables) so the two can't disagree.
 *
 * Unlike the other three Right Panel tabs, this one has no Plan/Actual split
 * (OP-2333) — break-even/risk analysis is inherently a forward-looking
 * planning tool, so it's always computed from Plan-tagged tasks only; the
 * shared narrow-column Plan/Actual control at the top of the Right Panel has
 * no effect here. Break-even dots on the chart are interactive — hover to
 * see the exact break-even yield for that scenario.
 */
const FinancialRiskPanel: React.FC = () => {
  const navigateToFarmsView = useFarmsLinkNavigate();
  const {
    scopedInputs,
    orgInputs,
    isLoading: isInputsLoading,
  } = useScopedInputs();

  const activeGOrganisation = useSelector(
    (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
  );
  const activeTreeNodeKey = useSelector(
    (state: RootState) => state.farmReducer.activeTreeNodeKey
  );
  const chartVisible = useSelector(
    (state: RootState) =>
      state.insightsReducer.rightPanel.chartVisible[RIGHT_PANEL_TAB_FINANCIAL]
  );
  const { data: gUnits = [], isLoading: isGUnitsLoading } = useFetchGUnits(
    activeGOrganisation || null
  );

  const isLoading = isInputsLoading || isGUnitsLoading;
  const isPlanScope = !!activeTreeNodeKey?.startsWith(INPUT_KEY_PREFIX);

  const risk = useMemo<FinancialRiskResult | null>(
    () =>
      isLoading
        ? null
        : computeFinancialRisk(scopedInputs, gUnits, PLAN_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );

  // "This plan's GM" is only unambiguous with one plan open, so the crop
  // average comparison is shown at plan scope only. Same Plan basis as the
  // rest of this panel.
  const cropAverage = useMemo<CropAverageGm | null>(
    () =>
      isLoading || !isPlanScope || scopedInputs.length !== 1
        ? null
        : computeCropAverageGm(
            scopedInputs[0],
            orgInputs,
            gUnits,
            PLAN_ONLY_CATEGORIES
          ),
    [isLoading, isPlanScope, scopedInputs, orgInputs, gUnits]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-base text-secondary-dark">Loading...</span>
      </div>
    );
  }

  if (!risk?.hasData) {
    return (
      <div className="flex items-center justify-center py-8 text-center">
        <span className="text-base text-secondary-dark">No financial data</span>
      </div>
    );
  }

  const gmPositive = risk.estNetGrossMargin >= 0;
  // Total Spend + Est. Net Gross Margin, then the optional comparison rows.
  const breakEvenStripeOffset =
    2 + (cropAverage ? cropAverageRowCount(cropAverage) : 0);

  return (
    <div className="flex flex-col gap-4">
      {/* ── Key metrics table ── */}
      {/* Alternating shading (rather than divider lines) separates rows; the
          stripe runs continuously across the whole table — section headings
          are excluded from it. */}
      <table className="w-full text-base">
        <tbody>
          <tr className={rowShade(0)}>
            <td className="py-1 pr-3 text-label text-charcoal">Total Spend</td>
            <td className="py-1 text-right tabular-nums text-charcoal">
              <DollarCell hasData={risk.hasData} value={risk.totalSpend} />
            </td>
          </tr>

          <tr className={rowShade(1)}>
            <td className="py-1 pr-3 text-label text-charcoal">
              Est. Net Gross Margin
            </td>
            <td className="py-1 text-right tabular-nums font-medium">
              <DollarCell
                hasData={risk.hasData}
                value={risk.estNetGrossMargin}
                colorClass={gmPositive ? 'text-success' : 'text-danger'}
              />
            </td>
          </tr>

          {cropAverage && (
            <CropAverageRows
              comparison={cropAverage}
              stripeOffset={2}
            />
          )}

          <BreakEvenRows risk={risk} stripeOffset={breakEvenStripeOffset} />
        </tbody>
      </table>

      <LinkButton
        onClick={() =>
          navigateToFarmsView({
            tab: INPUT_DOC_TAB_INSIGHTS,
            insightsLens: INPUT_DOC_LENS_MARGIN_BREAK_EVEN,
          })
        }
      >
        {LINK_FULL_SENSITIVITY_LABEL}
      </LinkButton>

      {/* ── Break-even chart ── */}
      {chartVisible && <BreakEvenChartWithLegend risk={risk} />}
    </div>
  );
};

export default FinancialRiskPanel;
